import React from "react";
import RowHeader from "./RowHeader";
import GridCell from "./GridCell";

/**
 * GridRow
 * One `<tr>` and all its cells, including the leftmost row-number TH.
 * Encapsulates per-row decisions: which cells are visible (hidden cols),
 * which cells are merge slaves (skipped), and the per-cell sticky style
 * computed from freeze offsets.
 */
// Shared immutable style for non-frozen cells — stable identity so
// React.memo(GridCell) sees an unchanged prop across renders.
const EMPTY_STICKY = Object.freeze({});

function GridRow({
  rowIndex, row, rowHeight, trClassName,
  // Cell data (cellStyles = border-processed map from GridBody)
  cellStyles, cellRichHtml, cellMerges, cellLinks,
  hiddenCols,
  // Selection state
  selectedCell, normalizedSelection, selectionType,
  // Hidden indicators
  isLastVisibleBeforeHidden,
  // Column metadata
  colWidths,
  // Freeze pane state for this row
  isFrozenRow, isLastFrozenRow, frozenCols,
  measuredTheadHeight, frozenRowOffset, frozenColOffsets,
  // Selection / row handlers
  onSelectRow, onHeaderContextMenu, onStartResize,
  onStartSelection, onExtendSelection,
  onLinkClick, onCellContextMenu,
}) {
  const rowHeaderClass = [
    selectionType === "row" && rowIndex >= normalizedSelection.r1 && rowIndex <= normalizedSelection.r2
      ? "selected-header" : "",
    isFrozenRow ? "frozen-row-header" : "",
  ].join(" ").trim();

  const rowHeaderStyle = {
    height: `${rowHeight}px`,
    ...(isFrozenRow
      ? { position: "sticky", top: `${measuredTheadHeight + (frozenRowOffset || 0)}px`, left: 0, zIndex: 15 }
      : {}),
  };

  return (
    <tr
      key={`row-${rowIndex}`}
      className={[trClassName, isLastFrozenRow ? "freeze-row-border" : ""].join(" ").trim()}
      style={{ height: `${rowHeight}px` }}
    >
      <RowHeader
        rowIndex={rowIndex}
        rowHeight={rowHeight}
        className={rowHeaderClass}
        style={rowHeaderStyle}
        onClick={(e) => onSelectRow(rowIndex, e)}
        onContextMenu={(e) => onHeaderContextMenu(e, "row", rowIndex)}
        onStartResize={onStartResize}
      />
      {row.map((cell, colIndex) => {
        if (hiddenCols.has(colIndex)) return null;
        const key = `${rowIndex}:${colIndex}`;
        const mergeInfo = cellMerges[key];
        if (mergeInfo === null) return null; // slave cell of a merged region

        // Compute effective row/col span respecting hidden rows/cols
        const mergeSpan = mergeInfo || {};
        let effectiveColSpan = mergeSpan.colSpan || 1;
        if (effectiveColSpan > 1) {
          let visibleSpan = 0;
          for (let ci = colIndex; ci < colIndex + effectiveColSpan; ci++) {
            if (!hiddenCols.has(ci)) visibleSpan++;
          }
          effectiveColSpan = Math.max(1, visibleSpan);
        }
        const effectiveRowSpan = mergeSpan.rowSpan || 1;

        // Border post-processing happens once per sheet in GridBody
        // (processedStyles) — here we just look the stable object up.
        // PERF: a per-render `{...spread}` here would give every cell a
        // new style identity and defeat React.memo(GridCell).
        const cleanCellStyle = cellStyles[key];

        const isFrozenCol = colIndex < frozenCols;
        const isLastFrozenCol = colIndex === frozenCols - 1;

        // Sticky style for frozen cells.  Frozen cells must have a solid
        // background so scrolled content beneath them does not show
        // through.  Non-frozen cells share ONE frozen empty object so
        // their prop identity never changes (React.memo).
        let stickyStyle = EMPTY_STICKY;
        if (isFrozenCol || isFrozenRow) {
          stickyStyle = {};
          if (isFrozenCol) {
            stickyStyle.position = "sticky";
            stickyStyle.left = `${frozenColOffsets[colIndex] || 0}px`;
            stickyStyle.zIndex = 6;
          }
          if (isFrozenRow) {
            stickyStyle.position = "sticky";
            stickyStyle.top = `${measuredTheadHeight + (frozenRowOffset || 0)}px`;
            stickyStyle.zIndex = isFrozenCol ? 8 : 7;
          }
          const bgColor = (cleanCellStyle && cleanCellStyle.backgroundColor) || "#ffffff";
          stickyStyle.backgroundColor = bgColor;
          stickyStyle["--cell-gap-shadow"] = `0 0 0 1.5px ${bgColor}`;
        }

        const inSelection =
          rowIndex >= normalizedSelection.r1 && rowIndex <= normalizedSelection.r2 &&
          colIndex >= normalizedSelection.c1 && colIndex <= normalizedSelection.c2;

        const className = [
          selectedCell.row === rowIndex && selectedCell.col === colIndex ? "selected-cell" : "",
          inSelection
            ? `in-selection ${rowIndex === normalizedSelection.r1 ? "sel-top" : ""} ${rowIndex === normalizedSelection.r2 ? "sel-bottom" : ""} ${colIndex === normalizedSelection.c1 ? "sel-left" : ""} ${colIndex === normalizedSelection.c2 ? "sel-right" : ""}`
            : "",
          cellLinks[key] ? "has-link" : "",
          isLastVisibleBeforeHidden(colIndex) ? "hidden-col-after" : "",
          (isFrozenRow || isFrozenCol) ? "frozen-cell" : "",
          isLastFrozenCol ? "freeze-col-border" : "",
          isLastFrozenRow ? "freeze-row-border" : "",
        ].join(" ").trim();

        const linkUrl = cellLinks[key];

        return (
          <GridCell
            key={`cell-${rowIndex}-${colIndex}`}
            rowIndex={rowIndex}
            colIndex={colIndex}
            cellValue={cell}
            html={cellRichHtml[key]}
            linkUrl={linkUrl}
            rowHeight={rowHeight}
            colWidth={colWidths[colIndex]}
            effectiveColSpan={effectiveColSpan}
            effectiveRowSpan={effectiveRowSpan}
            cellStyle={cleanCellStyle}
            stickyStyle={stickyStyle}
            className={className}
            onStartSelection={onStartSelection}
            onExtendSelection={onExtendSelection}
            onLinkClick={onLinkClick}
            onCellContextMenu={onCellContextMenu}
          />
        );
      })}
    </tr>
  );
}

export default React.memo(GridRow);
